/**
 * Typography config.
 *
 * One superfamily covers both scripts: Anek Latin + Anek Bangla (Ek Type,
 * variable, OFL). Designed together, so switching language doesn't change the
 * app's personality — which a Latin/Bengali mix-and-match always does.
 *
 * Loading strategy matters more than the choice: Bengali webfonts are heavy
 * (~180-250KB even as variable woff2) and CANNOT be safely character-subset,
 * because conjuncts (যুক্তাক্ষর) depend on glyph coverage + GSUB tables that
 * break when glyphs are stripped. So we load exactly one script — the active
 * locale's — and an `en` user never pays for Bengali at all.
 */

export type ScriptId = 'latin' | 'bengali';

export type FontFace = {
  family: string;
  /** Self-hosted woff2, relative to the app's public root. */
  src: string;
  /** Variable weight axis range. */
  weightRange: string;
  unicodeRange?: string;
};

export const fonts: Record<ScriptId, FontFace> = {
  latin: {
    family: 'Anek Latin',
    src: '/fonts/anek-latin-variable.woff2',
    weightRange: '400 700',
    unicodeRange:
      'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, ' +
      'U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, ' +
      'U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
  },
  bengali: {
    family: 'Anek Bangla',
    src: '/fonts/anek-bangla-variable.woff2',
    weightRange: '400 700',
    unicodeRange:
      'U+0951-0952, U+0964-0965, U+0980-09FE, U+1CD0, U+1CD2, U+1CD5-1CD6, ' +
      'U+1CD8, U+1CE1, U+1CEA, U+1CED, U+1CF2, U+1CF5-1CF7, U+200C-200D, ' +
      'U+20B9, U+25CC, U+A8F1',
  },
};

/** Locale → script, so the shell knows which single font file to preload. */
export const scriptForLocale: Record<string, ScriptId> = {
  en: 'latin',
  bn: 'bengali',
};

/**
 * Numerals.
 *
 * Decision: Western digits (4,820) everywhere, including Bangla UI.
 * The product's identity is numeric — Power Level, streaks, share cards — and
 * Western digits are universally read in Bangladesh, more compact, and travel
 * better on shared images. Exposed as a token so it can flip if students
 * disagree, without hunting through formatting code.
 */
export const numeralSystem: 'latn' | 'beng' = 'latn';

export const fontStack = {
  /**
   * Both real families are named, then both metric-matched fallbacks, then the
   * generics. The browser resolves per CHARACTER by glyph coverage, so a Latin
   * character takes Anek Latin (or its fallback) and a Bengali one falls past
   * both to Anek Bangla — which is why the order is real-real-fallback-fallback
   * rather than interleaved.
   *
   * Only one real family is ever downloaded: the two @font-face rules carry
   * disjoint unicode-range.
   */
  sans:
    `'Anek Latin', 'Anek Bangla', ` +
    `'Anek Latin Fallback', 'Anek Bangla Fallback', ` +
    `system-ui, -apple-system, 'Segoe UI', sans-serif`,
  mono: `ui-monospace, 'Cascadia Code', 'Source Code Pro', monospace`,
} as const;

/**
 * METRIC-MATCHED FALLBACKS — the CLS fix.
 *
 * The Bengali face is 152KB. With `font-display: swap` the page first paints in
 * whatever the platform picks, then relays out when Anek arrives, and a page of
 * Bengali prose shifts under the reader's eyes. That is the site's one real
 * Core Web Vitals weakness, and this audience reads on slow connections.
 *
 * Two ways out. `font-display: optional` gives zero shift but throws the brand
 * typography away on every first visit — the wrong trade for a publication.
 * The better one is to make the fallback *occupy the same space*, so the swap
 * is invisible and `swap` stays safe.
 *
 * Two knobs, and which matters depends on the CSS:
 *
 *  - `size-adjust` scales glyphs and advance widths. Because our stylesheets
 *    set an explicit unitless `line-height` everywhere, the line box height is
 *    already fixed — so what actually moves the page is TEXT WRAPPING. A
 *    fallback with wider glyphs wraps a paragraph to nine lines instead of
 *    eight and the block grows. This is the load-bearing one.
 *  - `ascent-override` / `descent-override` fix the baseline inside that line
 *    box, and matter wherever leading is tight enough for the content box to
 *    exceed the line box (the clamped headings).
 *
 * Every number below is MEASURED, not guessed: Anek's come from the shipped
 * woff2 files (head/hhea/OS2 are not woff2-transformed, so they can be read
 * after a plain brotli decompress), Noto Sans Bengali's from the Google Fonts
 * release, Arial's from its published metrics. The arithmetic is left in so a
 * future font swap is a matter of changing the inputs.
 */
type FontMetrics = {
  unitsPerEm: number;
  ascent: number;
  descent: number; // positive magnitude
  xHeight: number;
};

const measured = {
  anekBangla: { unitsPerEm: 2000, ascent: 2500, descent: 1232, xHeight: 978 },
  anekLatin: { unitsPerEm: 2000, ascent: 1800, descent: 400, xHeight: 978 },
  /** Android, and the Linux default. The fallback that actually matters here. */
  notoSansBengali: { unitsPerEm: 1000, ascent: 917, descent: 408, xHeight: 536 },
  /** Stands in for the Latin fallback across Windows/macOS/Android. */
  arial: { unitsPerEm: 2048, ascent: 1854, descent: 434, xHeight: 1062 },
} satisfies Record<string, FontMetrics>;

const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
const em = (m: FontMetrics, v: number) => v / m.unitsPerEm;

/**
 * Matching x-height rather than average advance width: it is the metric a
 * reader actually perceives as "the same size", and for these two scripts it
 * tracks advance width closely enough that wrapping lands within a word.
 *
 * The overrides are divided by sizeAdjust because the browser applies
 * `size-adjust` to every metric of the face, overridden ones included.
 */
function matchFallback(target: FontMetrics, fallback: FontMetrics) {
  const sizeAdjust = em(target, target.xHeight) / em(fallback, fallback.xHeight);
  return {
    sizeAdjust: pct(sizeAdjust),
    ascentOverride: pct(em(target, target.ascent) / sizeAdjust),
    descentOverride: pct(em(target, target.descent) / sizeAdjust),
    lineGapOverride: '0%',
  };
}

export type FallbackFace = {
  family: string;
  /** Platform faces to try, in order. `local()` only — never a network fetch. */
  locals: string[];
  sizeAdjust: string;
  ascentOverride: string;
  descentOverride: string;
  lineGapOverride: string;
};

export const fallbackFaces: FallbackFace[] = [
  {
    family: 'Anek Latin Fallback',
    locals: ['Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial'],
    ...matchFallback(measured.anekLatin, measured.arial),
  },
  {
    // Before this existed the stack named NO Bengali font at all — a Bangla
    // page fell through to `sans-serif` and took whatever the platform chose.
    family: 'Anek Bangla Fallback',
    locals: [
      'Noto Sans Bengali', // Android, Linux
      'Nirmala UI', // Windows
      'Bangla Sangam MN', // iOS, macOS
      'Kohinoor Bangla', // iOS
      'Shonar Bangla', // older Windows
    ],
    ...matchFallback(measured.anekBangla, measured.notoSansBengali),
  },
];
